import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Card, CardContent, CardHeader, Divider, IconButton } from '@mui/material';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { brandColors } from 'types/colors';

const HomePageCard = ({ title, subheader, content, footer, icon, screens, headerAction }) => {
  const hasScreens = Array.isArray(screens) && screens.length > 0;
  const [activeScreen, setActiveScreen] = useState(0);
  const safeActiveScreen = hasScreens ? Math.min(activeScreen, screens.length - 1) : 0;
  const canGoLeft = safeActiveScreen > 0;
  const canGoRight = hasScreens && safeActiveScreen < screens.length - 1;
  const sliderWidth = hasScreens ? `${screens.length * 100}%` : '100%';
  const paneWidth = hasScreens ? `${100 / screens.length}%` : '100%';
  const sliderTranslate = hasScreens ? `translateX(-${(safeActiveScreen * 100) / screens.length}%)` : 'translateX(0%)';

  const renderedContent = useMemo(() => {
    if (!hasScreens) return content;

    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
          <IconButton size="small" onClick={() => setActiveScreen(prev => prev - 1)} disabled={!canGoLeft}>
            <KeyboardArrowLeft />
          </IconButton>
          <IconButton size="small" onClick={() => setActiveScreen(prev => prev + 1)} disabled={!canGoRight}>
            <KeyboardArrowRight />
          </IconButton>
        </Box>
        <Box sx={{ overflow: 'hidden', width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              width: sliderWidth,
              transform: sliderTranslate,
              transition: 'transform 0.35s ease',
            }}>
            {screens.map((screenContent, index) => (
              <Box key={index} sx={{ width: paneWidth, flexShrink: 0 }}>
                {screenContent}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  }, [canGoLeft, canGoRight, content, hasScreens, paneWidth, screens, sliderTranslate, sliderWidth]);

  return (
    <Card
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        height: { lg: '100%' },
        minHeight: { lg: 0 },
        borderRadius: 3,
        overflow: 'hidden',
        border: `1px solid ${brandColors.GREY_LIGHT}`,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
      }}>
      <CardHeader
        avatar={
          icon ? (
            <Box className="homepage-card-icon" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {icon}
            </Box>
          ) : null
        }
        title={title}
        subheader={subheader}
        action={headerAction}
        sx={{
          background: brandColors.BLUE,
          pr: { xs: 0.7, sm: 'auto' },
          color: 'white',
          minHeight: {
            xs: 77,
            lg: 68,
          },
          '& .MuiCardHeader-avatar': { marginRight: 1 },
          '& .MuiCardHeader-content': { minWidth: 0 },
          '& .MuiCardHeader-action': {
            alignSelf: 'center',
            margin: 0,
            minWidth: 0,
          },
          '& .MuiCardHeader-avatar .homepage-card-icon svg': {
            width: 'fit-content',
            height: 48,
            fill: '#fff',
          },
          '& .MuiCardHeader-title': {
            fontWeight: 600,
            color: '#fff',
            '@media (min-width: 1200px) and (max-height: 900px)': {
              fontSize: '1rem',
            },
          },
          '& .MuiCardHeader-subheader': {
            color: '#fff',
            fontSize: '1rem',
            fontWeight: 300,
            '@media (min-width: 1200px) and (max-height: 900px)': {
              fontSize: '0.85rem',
            },
          },
          py: {
            xs: 1.5,
            lg: 1,
          },
          '@media (min-width: 1200px) and (max-height: 900px)': {
            minHeight: 58,
            py: 0.6,
            '& .MuiCardHeader-avatar .homepage-card-icon svg': {
              height: 46,
            },
          },
        }}
      />
      <Box sx={{ display: 'flex', alignItems: 'stretch', flex: { lg: 1 }, minWidth: 0, minHeight: { lg: 0 } }}>
        <CardContent
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            px: 'auto',
            py: {
              xs: 2.5,
            },
            justifyContent: 'center',
            alignItems: 'center',
            minWidth: 0,
            minHeight: { lg: 0 },
            overflowY: 'auto',
            overflowX: 'hidden',
          }}>
          {renderedContent}
        </CardContent>
        {footer ? (
          <>
            <Divider orientation="vertical" flexItem sx={{ borderColor: brandColors.GREY, borderWidth: '2px', m: 0 }} />
            <Box
              sx={{
                // minWidth: 140,
                px: 2.5,
                py: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: brandColors.TURQUOISE,
              }}>
              {footer}
            </Box>
          </>
        ) : null}
      </Box>
    </Card>
  );
};

HomePageCard.propTypes = {
  title: PropTypes.node,
  subheader: PropTypes.node,
  content: PropTypes.node,
  footer: PropTypes.node,
  icon: PropTypes.node,
  screens: PropTypes.arrayOf(PropTypes.node),
  headerAction: PropTypes.node,
};

export default HomePageCard;
